import { useReactiveVar } from '@apollo/client'
import { Flex } from '@radix-ui/themes'
import { privyEmbeddedAddressesVar } from 'features/auth-privy/store'
import { DataTestIDs } from 'shared/constants'
import { usePlayWallet } from 'shared/hooks/use-play-wallet'
import { WalletIcon } from 'shared/icons'
import { openWalletDeposit } from 'shared/store/wallet-deposit'
import { DialogShared } from 'shared/ui'
import { useLinkWallet } from '../hooks/use-link-wallet'
import { useWalletActions } from '../hooks/use-wallet-actions'
import { useWalletBalances } from '../hooks/use-wallet-balances'
import { isWalletsDialogOpenVar } from '../store/dialogs'
import { DialogDecorations } from './dialog-decorations'
import { LinkWalletSection } from './link-wallet-section'
import { NeonDialogHeader } from './neon-dialog-header'
import { PlayWalletNotice } from './play-wallet-notice'
import { PrivyExportSection } from './privy-export-section'
import { WalletListItem } from './wallet-list-item'
import styles from '../neon-wallets.module.scss'

// NOTE: one dialog at a time; the Deposit dialog replaces this one
const handleDeposit = (address: string) => {
  isWalletsDialogOpenVar(false)
  openWalletDeposit(address)
}

/**
 * Your wallets (DEV-323): the account's wallets with the one selected for
 * play, why the connected wallet cannot play (not linked / not selected),
 * linking another wallet by signature, the way to fund a wallet (Top up)
 * and, with Privy, the embedded-key export. Neon surface of Bull's Eye.
 * Mounted once in `App`; opened from the header, Settings → Wallets and the
 * game buttons.
 */
export const WalletsDialog: React.FC = () => {
  const { wallets, selectedAddress, connectedAddress, status, selectWallet } =
    usePlayWallet()
  const { linkConnectedWallet, linkProviderWallet, isLinking } = useLinkWallet()
  const balances = useWalletBalances()
  const { pending, makePrimary, unlink } = useWalletActions()
  const embeddedAddresses = useReactiveVar(privyEmbeddedAddressesVar)
  // NOTE: a user who signed in with Phantom has no embedded wallet to export
  const embeddedAddress = wallets
    .map(wallet => wallet.address)
    .find(address => embeddedAddresses.includes(address))

  return (
    <DialogShared
      openStore={isWalletsDialogOpenVar}
      width='68rem'
      className={styles.dialog}
    >
      <DialogDecorations />
      <Flex
        direction='column'
        gap='4'
        className={styles.dialogBody}
        data-testid={DataTestIDs.walletsDialog}
      >
        <NeonDialogHeader
          icon={<WalletIcon />}
          title={
            <>
              Your <b>wallets</b>
            </>
          }
          subtitle='Pick the wallet you play with and keep it topped up.'
        />

        <PlayWalletNotice
          status={status}
          connectedAddress={connectedAddress}
          isLinking={isLinking}
          onLinkConnected={linkConnectedWallet}
          onSelectConnected={selectWallet}
        />

        <Flex
          direction='column'
          gap='3'
        >
          {wallets.map(wallet => (
            <WalletListItem
              key={wallet.id}
              address={wallet.address}
              isPrimary={wallet.isPrimary}
              isSelected={wallet.address === selectedAddress}
              isConnected={wallet.address === connectedAddress}
              isEmbedded={embeddedAddresses.includes(wallet.address)}
              balance={balances[wallet.address]}
              onSelect={selectWallet}
              onDeposit={handleDeposit}
              manage={{
                isBusy: pending !== null,
                pendingAction:
                  pending?.address === wallet.address ? pending.action : null,
                onMakePrimary: makePrimary,
                onUnlink: unlink
              }}
            />
          ))}
        </Flex>

        <LinkWalletSection
          isLinking={isLinking}
          onLink={linkProviderWallet}
        />

        {embeddedAddress ?
          <PrivyExportSection address={embeddedAddress} />
        : null}
      </Flex>
    </DialogShared>
  )
}
